import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaTimes, FaLock, FaBolt, FaStar, FaChevronDown, FaInfoCircle, FaClock } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import CheckoutModal from "./Checkout";

export default function Billing({ onClose, onSubscriptionSuccess }) {
  const navigate = useNavigate();
  const tenantId = localStorage.getItem("tenant_id");
  
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [confirmPlan, setConfirmPlan] = useState(null);
  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/tenant/${tenantId}/overview`)
      .then(res => res.json())
      .then(data => {
        setOverview(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [tenantId]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-md"
      >
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
              className="h-12 w-12 rounded-full border-3 border-[#5B3A60]/30 border-t-[#5B3A60]"
            />
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="absolute inset-0 m-auto h-2.5 w-2.5 rounded-full bg-[#5B3A60]"
            />
          </div>
          <div className="text-center">
            <p className="text-white text-sm font-semibold">Loading subscription details</p>
            <motion.div
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              className="text-white/60 text-xs mt-1"
            >
              Please wait...
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error || !overview) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-sm text-red-500 p-4 bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="text-red-500 text-2xl" />
          </div>
          <p className="text-gray-700 mb-4">
            Unable to load billing information. Please check your connection or{" "}
            <button 
              onClick={() => window.location.reload()} 
              className="text-indigo-600 hover:text-indigo-700 font-semibold underline transition-colors"
            >
              try again
            </button>.
          </p>
        </div>
      </div>
    );
  }
  const rawPlan =
  overview.subscription_tier?.toLowerCase()?.split("_")[0] || "free";

const currentPlan =
  rawPlan === "trial" ? "free" : rawPlan;

const isTrialPlan =
  rawPlan === "trial";

const isActiveSub =
  overview.subscription_status?.toLowerCase() === "active" &&
  !isTrialPlan;

  // const currentPlan =  overview.subscription_tier?.toLowerCase()?.split("_")[0] || "free";
  const planOrder = ["free", "starter", "pro", "enterprise"];
  const currentPlanIndex = planOrder.indexOf(currentPlan);
  const hasUsedFree = currentPlan !== "free";
  const isExpired = overview.period_end && new Date().getTime() > new Date(overview.period_end).getTime();
  // const isActiveSub = overview.subscription_status?.toLowerCase() === "active";
  const pendingPlan = overview.pending_plan;
  const pendingPlanStart = overview.pending_plan_start;

  const isCancelled =
    overview.subscription_status?.toLowerCase() === "cancelled";

  const periodEnd = overview.period_end
    ? new Date(overview.period_end).getTime()
    : null;

  const now = new Date().getTime();

  const isCancellationLocked =
    isCancelled &&
    periodEnd &&
    now < periodEnd;
  

  const featureDescriptions = {
  "1 Agent": "Run a single AI agent to handle conversations",
  "2 Agents": "Run multiple agents for different use cases",
  "3 Agents": "Deploy multiple agents for advanced workflows",
  "Unlimited bots": "Scale with unlimited AI agents",
  "100 messages": "Monthly limit of responses your agent can send",
  "4,000 messages / month": "Higher response capacity for growing usage",
  "10,000 messages / month": "Handle large-scale customer conversations",
  "Custom message volume": "Flexible limits tailored to your needs",
  "10 page crawls": "Pages indexed to build your knowledge base",
  "500 page crawls / month": "Expand your knowledge base coverage",
  "1,000 page crawls / month": "Build a comprehensive knowledge base",
  "Custom page crawl volume": "Adjust crawl limits based on usage",
  "50 MB storage": "Storage for documents and training data",
  "1 GB storage": "More space for content and training data",
  "5 GB storage": "Store large volumes of data and documents",
  "Custom storage": "Storage scaled to your requirements",
  "Basic analytics": "Simple insights into usage and conversations",
  "Standard analytics": "Detailed performance and usage metrics",
  "Advanced analytics": "Deep insights with detailed reporting tools",
  "Full analytics + data export": "Advanced reporting with export capabilities",
  "Community support": "Access help through shared community channels",
  "Email support — 24h response": "Get support responses within a day",
  "Priority support": "Faster issue resolution from support team",
  "Priority support — 12h response": "Faster support with priority handling",
  "Dedicated account manager": "Personal support for your business",
  "Shared account manager": "Guidance from a dedicated success manager",
  "Custom branding": "Apply your brand identity to the chatbot",
  "Agent handoff": "Transfer chats from bot to human agents",
  "Integrations": "Connect with external tools and platforms",
  "Integrations (add-on)": "Integrations with external tools",
  "Integrations (included)": "All integrations included by default",
  "Dedicated onboarding": "Personalized setup assistance",
  "SLA guarantee": "Guaranteed uptime and support response times"
};

  const basePlans = [
    {
      id: "free",
      name: "Free",
      tagline: "Try Droog with no commitment",
      features: [
        {
          items: [
            { text: "1 Agent", included: true },
            { text: "100 messages", included: true },
            { text: "10 page crawls", included: true },
            { text: "50 MB storage", included: true }
          ]
        },
        {
          items: [
            { text: "Basic analytics", included: true },
            { text: "Community support", included: true },
            { text: "Custom branding", included: false },
            { text: "Priority support", included: false }
          ]
        },
        {
          items: [
            { text: "Agent handoff", included: false },
            { text: "Integrations", included: false }
          ]
        }
      ],
      pricing: {
        monthly: { price: "₹0", period: "/forever", cta: "Get started" },
        yearly: { price: "₹0", period: "/forever", cta: "Get started" }
      },
      highlight: false,
      darkMode: false,
      width: "w-full",
      height: "min-h-[520px]",
      padding: "p-5",
      titleSize: "text-lg",
      priceSize: "text-2xl",
      shadow: "shadow-md hover:shadow-lg",
      border: "border border-slate-200",
      bg: "bg-white"
    },
    {
      id: "starter",
      name: "Starter",
      tagline: "For growing business deploying their first support agent",
      features: [
        {
          items: [
            { text: "2 Agents", included: true },
            { text: "4,000 messages / month", included: true },
            { text: "500 page crawls / month", included: true },
            { text: "1 GB storage", included: true }
          ]
        },
        {
          items: [
            { text: "Standard analytics", included: true },
            { text: "Email support — 24h response", included: true },
            { text: "Custom branding", included: true },
          ]
        },
        {
          items: [
            { text: "Agent handoff", included: true },
            { text: "Integrations (add-on)", included: true },
            { text: "Priority support", included: false }
          ]
        }
      ],
      pricing: {
        monthly: { price: "₹5.9K", period: "/month", cta: "Get Started", original: null, saveText: null },
        yearly: { price: "₹4.9K", period: "/month", cta: "Get Started", original: "₹59,990", saveText: "Save ₹11,998" }
      },
      highlight: true,
      darkMode: false,
      width: "w-full",
      height: "",
      padding: "p-6",
      titleSize: "text-xl",
      priceSize: "text-3xl",
      shadow: "shadow-xl hover:shadow-2xl",
      border: "border-2 border-indigo-500",
      bg: "bg-gradient-to-b from-indigo-50/40 to-white"
    },
    {
      id: "pro",
      name: "Pro",
      tagline: "Full power for teams handling thousands of conversations",
      features: [
        {
          items: [
            { text: "3 Agents", included: true },
            { text: "10,000 messages / month", included: true },
            { text: "1,000 page crawls / month", included: true },
            { text: "5 GB storage", included: true }
          ]
        },
        {
          items: [
            { text: "Advanced analytics", included: true },
            { text: "Priority support — 12h response", included: true },
            { text: "Shared account manager", included: true },
            { text: "Custom branding", included: true }
          ]
        },
        {
          items: [
            { text: "Agent handoff", included: true },
            { text: "Integrations (add-on)", included: true },
            { text: "Dedicated onboarding", included: true }
          ]
        }
      ],
      pricing: {
        monthly: { price: "₹12.9K", period: "/month", cta: "Get Started", original: null, saveText: null },
        yearly: { price: "₹10.8K", period: "/month", cta: "Get Started", original: "₹1,29,990", saveText: "Save ₹25,998" }
      },
      highlight: false,
      darkMode: true,
      width: "w-full",
      height: "",
      padding: "p-7",
      titleSize: "text-2xl",
      priceSize: "text-4xl",
      shadow: "shadow-2xl hover:shadow-3xl",
      border: "border border-indigo-400/30",
      bg: "bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900"
    },
    {
      id: "enterprise",
      name: "Enterprise",
      tagline: "Bespoke solutions for large organizations",
      features: [
        {
          items: [
            { text: "Unlimited bots", included: true },
            { text: "Custom message volume", included: true },
            { text: "Custom page crawl volume", included: true },
            { text: "Custom storage", included: true }
          ]
        },
        {
          items: [
            { text: "Full analytics + data export", included: true },
            { text: "Dedicated account manager", included: true },
            { text: "SLA guarantee", included: true },
            { text: "Custom branding", included: true }
          ]
        },
        {
          items: [
            { text: "Agent handoff", included: true },
            { text: "Integrations (included)", included: true }
          ]
        }
      ],
      pricing: {
        monthly: { price: "Custom", period: "", cta: "Contact sales" },
        yearly: { price: "Custom", period: "", cta: "Contact sales" }
      },
      highlight: false,
      darkMode: false,
      width: "w-full",
      height: "",
      padding: "p-6",
      titleSize: "text-lg",
      priceSize: "text-2xl",
      shadow: "shadow-lg hover:shadow-xl",
      border: "border border-slate-300",
      bg: "bg-gradient-to-b from-slate-50 to-white"
    }
  ];

  const plans = basePlans.map((plan) => {
    const planTier = plan.id;
    const planCycle = billingCycle;

    const currentTier = currentPlan;
    const currentCycle =
      overview.billing_cycle === "annual" ? "yearly" : "monthly";

    const isSamePlan = planTier === currentTier;
    const isSameCycle = planCycle === currentCycle;

    const hasPendingPlan = pendingPlan && pendingPlanStart;

    let isDisabled = false;
    let cta = plan.pricing[planCycle].cta;
    let isCancelButton = false;

    if (isCancellationLocked) {
  isDisabled = true;
  isCancelButton = false;

  if (isSamePlan && isSameCycle) {
    cta = "Cancelled";
  } else {
    cta = plan.pricing[planCycle].cta;
  }
}
else if (planTier === "free") {
  isDisabled = true;
}
else if (hasPendingPlan) {
  isDisabled = true;
  cta = "Scheduled";
}
else if (isActiveSub && isSamePlan && isSameCycle) {
  isDisabled = false;
  isCancelButton = true;
  cta = "Cancel Subscription";
}
else if (isActiveSub) {
  isDisabled = true;
}
else if (isSamePlan && isSameCycle) {
  isDisabled = true;
}

    return {
      ...plan,
      ...plan.pricing[planCycle],
      isCurrent: isSamePlan && isSameCycle,
      isDisabled,
      isCancelButton,
      cta
    };
});
  
  const handleUpgrade = async (plan) => {
    if (plan.isDisabled) return;

    if (plan.id === "enterprise") {
      window.location.href = "mailto:contact@droog.io";
      return;
    }

    try {
      const planCode = `${plan.id}_${billingCycle === "yearly" ? "annual" : "monthly"}_v1`;
      const currentTier = currentPlan;
      const currentCycle = overview.billing_cycle === "annual" ? "yearly" : "monthly";
      
      const isUpgrade = planOrder.indexOf(plan.id) > planOrder.indexOf(currentTier);
      const isSamePlan = plan.id === currentTier;
      const isMonthlyToAnnual = currentCycle === "monthly" && billingCycle === "yearly";

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/subscriptions/manage-change?tenant_id=${tenantId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ plan_code: planCode }),
        }
      );

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to process plan change");
      }

      const data = await res.json();

      if (data.action === "immediate_upgrade") {
        setConfirmPlan({
          ...plan,
          plan_code: planCode,
          isScheduled: false,
          effective_at: null
        });
      } 
      else if (data.action === "scheduled_change") {
        setConfirmPlan({
          ...plan,
          plan_code: planCode,
          isScheduled: true,
          effective_at: data.effective_at
        });
      } 
      else if (data.action === "no_change") {
        alert("You are already on this plan.");
      } 
      else {
        const shouldSchedule = !isUpgrade || (isSamePlan && isMonthlyToAnnual);
        setConfirmPlan({
          ...plan,
          plan_code: planCode,
          isScheduled: shouldSchedule,
          effective_at: data.effective_at || null
        });
      }

    } catch (err) {
      console.error("Upgrade error:", err);
      alert(err.message || "Something went wrong");
    }
  };

  const handleCancelSubscription = async () => {
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/subscriptions/cancel?tenant_id=${tenantId}&cancel_immediately=false`,
      {
        method: "POST"
      }
    );

    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Cancel failed");
    }

    alert("Subscription cancelled successfully");
    window.location.reload();
  } catch (err) {
    alert(err.message || "Something went wrong");
  }
};

  const PlanCard = ({ plan, index }) => (
    <motion.div
      initial={{ opacity: 0, y: 25 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -6 }}
      className={`${plan.width} ${plan.height} ${plan.padding} ${plan.shadow} ${plan.border} ${plan.bg} rounded-2xl flex flex-col relative z-10 transition-all duration-300 overflow-visible`}
    >
      {plan.highlight && (
        <motion.div 
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-bold px-3.5 py-1 rounded-full shadow-md flex items-center gap-1.5 whitespace-nowrap z-10"
        >
          <FaStar size={9} />
          Most Popular
        </motion.div>
      )}

      <div className="flex flex-col">
        <div className="mb-5">
          <div className="flex items-center gap-2">
            <h3 className={`${plan.titleSize} font-bold ${plan.darkMode ? "text-white" : "text-slate-900"}`}>
              {plan.name}
            </h3>
            {plan.isCurrent && (
              <div className="relative group">
                <FaCheck className="text-emerald-500" size={12} />
                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 
                                opacity-0 group-hover:opacity-100 
                                transition-all duration-150 z-50">
                  <div className="bg-black text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                    Your plan
                  </div>
                </div>
              </div>
            )}
          </div>
          <p className={`text-sm ${plan.darkMode ? "text-slate-300" : "text-slate-600"} leading-relaxed`}>
            {plan.tagline}
          </p>
        </div>

        <div className="mb-6">
          <div className="flex items-baseline">
            <span className={`${plan.priceSize} font-bold ${plan.darkMode ? "text-white" : "text-slate-900"}`}>
              {plan.price}
            </span>
            <span className={`ml-1.5 text-sm ${plan.darkMode ? "text-slate-400" : "text-slate-500"}`}>
              {plan.period}
            </span>
          </div>
          
          {billingCycle === 'yearly' && plan.saveText && (
             <div className="text-sm text-emerald-600 font-semibold mt-2">{plan.saveText}</div>
          )}
        </div>

        <motion.button
          whileHover={{ scale: plan.isDisabled ? 1 : 1.01 }}
          whileTap={{ scale: plan.isDisabled ? 1 : 0.99 }}
          onClick={() =>
            plan.isCancelButton
              ? handleCancelSubscription()
              : handleUpgrade(plan)
          }
          disabled={plan.isDisabled}
          className={`w-full py-3.5 px-4 rounded-xl font-semibold text-sm mb-6 transition-all ${
            plan.isDisabled
              ? "bg-slate-100 text-slate-400 cursor-not-allowed"
              : plan.darkMode
              ? "bg-white text-slate-900 hover:bg-slate-100"
              : "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700"
          }`}
        >
          {plan.cta}
        </motion.button>
        
        <div className={`space-y-3 ${plan.darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
          {plan.features.map((section, sIdx) => (
            <div key={sIdx} className={`${section.title ? 'mb-2' : 'mb-1'}`}>
              {section.title && (
                <div className="text-xs font-bold tracking-wide text-slate-400 mb-1">
                  {section.title}
                </div>
              )}
              <div className="space-y-2">
                {(section.items || []).map((feature, idx) => (
                  <div key={idx} className="flex items-start text-sm relative overflow-visible">
                    <div className={`mr-3 mt-0.5 flex-shrink-0 ${
                      feature.included
                        ? (plan.darkMode ? 'text-emerald-400' : 'text-emerald-600')
                        : 'text-slate-300'
                    }`}>
                      <FaCheck size={13} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`${!feature.included ? 'text-slate-400' : 'font-medium'}`}>
                        {feature.text.includes("response") ? (
                          <>
                            Email support — 24h response{" "}
                            <span className="relative group inline-flex items-center ml-1">
                              <FaInfoCircle
                                className="text-slate-400 hover:text-indigo-500 transition cursor-pointer"
                                size={12}
                              />
                              <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 w-52 opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-150 ease-out z-50">
                                <div className="rounded-lg bg-white/95 backdrop-blur-md text-slate-700 text-xs px-3 py-2 shadow-lg border border-slate-200">
                                  {featureDescriptions[feature.text]}
                                </div>
                                <div className="w-2 h-2 bg-white/95 rotate-45 absolute -top-1 left-1/2 -translate-x-1/2 border-l border-t border-slate-200"></div>
                              </div>
                            </span>
                          </>
                        ) : (
                          feature.text
                        )}
                      </span>
                      {feature.included && 
                       featureDescriptions[feature.text] && 
                       !feature.text.includes("response") && (
                        <div className="relative group cursor-pointer">
                          <FaInfoCircle className="text-slate-400 hover:text-indigo-500 transition" size={12} />
                          <div className="pointer-events-none absolute right-full mr-2 top-1/2 -translate-y-1/2 w-[160px] opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 ease-out z-50">
                            <div className="rounded-lg bg-white/95 backdrop-blur-md text-slate-700 text-[11px] px-2.5 py-1.5 leading-snug shadow-md border border-slate-200 break-words">
                              {featureDescriptions[feature.text]}
                            </div>
                            <div className="w-2 h-2 bg-white/95 rotate-45 absolute right-[-4px] top-1/2 -translate-y-1/2 border-r border-t border-slate-200"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  const faqs = [
    {
      id: "limit",
      q: "What happens when I hit my monthly message limit?",
      a: "Your agents will stop responding to new conversations until the next billing cycle. You can purchase extra message credits any time from the add-ons section to avoid any downtime."
    },
    {
      id: "plan-change",
      q: "Can I change my plan at any time?",
      a: "Yes. You can upgrade at any point of time, your new limits apply right away. Downgrades take effect at the start of your next billing cycle."
    },
    {
      id: "crawl",
      q: "What is a 'page crawl'?",
      a: "A page crawl is when Droog reads and indexes a page from your website to build your agent's knowledge base. Each page indexed counts as one crawl credit."
    },
    {
      id: "rollover",
      q: "Do unused messages or crawls roll over to the next month?",
      a: "No, limits reset at the start of each billing cycle. If you regularly hit your limit, consider upgrading or purchasing add-ons."
    },
    {
      id: "gst",
      q: "Is GST included in the prices shown?",
      a: "No. All prices shown are exclusive of GST at 18%."
    },
    {
      id: "downgrade",
      q: "What happens to my agents if I downgrade?",
      a: "If your new plan supports fewer agents than you currently have active, you'll be asked to choose which agents you prefer to keep. Inactive agents and their data are retained for 90 days before deletion."
    },
    {
      id: "trial",
      q: "Does the Starter free trial require a credit card?",
      a: "No credit card is needed to start your free trial. You'll only be asked for payment details when you decide to continue after the trial."
    },
    {
      id: "billing-switch",
      q: "Can I switch from monthly to annual mid-cycle?",
      a: "Yes. When you switch to annual at any time and the changes will take effect immediately."
    }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-y-auto overflow-x-hidden font-sans">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-indigo-200/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-20 w-72 h-72 bg-purple-200/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-blue-200/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center relative z-10">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            if (onClose) onClose();
            navigate(`/dashboard/${tenantId}`);
          }}
          className="absolute top-5 right-5 text-gray-400 hover:text-indigo-600 transition-colors p-2 rounded-full hover:bg-gray-100"
        >
          <FaTimes size={18} />
        </motion.button>

        <motion.span 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-xs font-bold tracking-widest text-indigo-600 uppercase mb-3 block"
        >
          Pricing Plans
        </motion.span>
        <motion.h1 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight"
        >
          Your agents are ready to scale, Are you ?
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-base text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed"
        >
          No commitment needed - start free, upgrade at your own pace, and access essential features on every plan, no matter how fast you grow.
        </motion.p>

        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center"
        >
          <div className="relative flex w-[340px] md:w-[360px] rounded-full bg-white/90 backdrop-blur-lg p-1.5 shadow-lg border border-slate-200/60">
            <motion.div
              layout
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className={`absolute top-1 bottom-1 w-1/2 rounded-full ${
                billingCycle === "monthly" ? "left-1" : "left-1/2"
              }`}
              style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)" }}
            />
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`relative z-10 w-1/2 py-2.5 text-sm font-semibold rounded-full transition ${
                billingCycle === "monthly"
                  ? "text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`relative z-10 w-1/2 py-2.5 text-sm font-semibold rounded-full flex items-center justify-center gap-1.5 transition ${
                billingCycle === "yearly"
                  ? "text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Annual
              <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">
                Save 2 months
              </span>
            </button>
          </div>
        </motion.div>
        
        <AnimatePresence>
          {billingCycle === 'yearly' && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-sm text-slate-500 mt-2 mb-8 font-medium text-center"
            >
              Pay for 10 months, get 12 — billed annually.
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 relative z-10">
        
        {isCancelled && overview.period_end && (
          <div className="max-w-4xl mx-auto mb-8 px-4 py-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <p>
              Your subscription has been cancelled. You can continue using your current plan until{" "}
              <span className="font-semibold">
                {new Date(overview.period_end).toLocaleString()}
              </span>.
              Other plans will be available after this time.
            </p>
          </div>
        )}

        {pendingPlan && pendingPlanStart && (
          <div className="max-w-4xl mx-auto mb-8 px-4 py-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm">
            <p>
              Your plan change to{" "}
              <span className="font-semibold">
                {pendingPlan}
              </span>{" "}
              is scheduled for{" "}
              <span className="font-semibold">
                {new Date(pendingPlanStart).toLocaleDateString()}
              </span>
            </p>
          </div>
        )}

        
        {isActiveSub && !pendingPlan && (
          <div className="max-w-xl mx-auto mb-8 px-4 py-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm text-center">
            Please cancel your current subscription first to subscribe to another plan.
          </div>
        )}
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 items-start">
          {plans.map((plan, index) => (
            <PlanCard key={plan.id} plan={plan} index={index} />
          ))}
        </div>

        <div className="mt-10 py-4 border-y border-slate-200/60 text-center space-y-2">
          <div className="flex justify-center gap-6 text-xs font-medium text-slate-600">
            <span className="flex items-center gap-1.5">
              <FaLock className="text-emerald-500" size={12} /> Secure payments
            </span>
            <span className="flex items-center gap-1.5">
              <FaCheck className="text-emerald-500" size={12} /> Cancel anytime
            </span>
            <span className="flex items-center gap-1.5">
              <FaCheck className="text-emerald-500" size={12} /> Support on every plan
            </span>
          </div>
          <p className="text-xs text-slate-400">
            All prices in INR, exclusive of GST (18%). Overages billed at cycle end.
          </p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-10 text-center"
        >
          <div className="inline-block px-3.5 py-1.5 bg-slate-100 rounded-full text-xs font-bold text-slate-700 mb-4">ADD-ONS</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Extend your plan</h2>
          <p className="text-slate-600 text-sm mb-6">Available on Starter and Pro. Pay only for what you use.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-5xl mx-auto">
            {[
              {
                title: "Extra messages",
                prices: [
                  { label: "Starter", value: "₹2.50 / message" },
                  { label: "Pro", value: "₹2.00 / message" }
                ]
              },
              {
                title: "Extra storage",
                prices: [{ value: "₹100 for 3 GB" }]
              },
              {
                title: "Crawl credits",
                prices: [{ value: "₹500 for 400 pages" }]
              }
            ].map((item, i) => (
              <div
                key={i}
                className="border border-slate-200 rounded-xl p-4 text-left hover:border-indigo-400 transition-colors bg-white shadow-sm"
              >
                <h4 className="font-semibold text-slate-900 text-sm mb-3">{item.title}</h4>
                {item.prices.map((p, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    {p.label && <span className="text-slate-500">{p.label}</span>}
                    <span className="font-bold text-indigo-600">{p.value}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-block px-3.5 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold mb-4">COMING SOON</div>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">On our roadmap</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto text-left">
            {[
              { icon: "🕸️", title: "Auto web crawling", desc: "Schedule automatic crawls of your website or documentation." },
              { icon: "🎙️", title: "Voice integrations", desc: "Connect bots to voice channels with natural spoken responses." },
              { icon: "🔌", title: "Third-party integrations", desc: "Native connectors for WhatsApp" }
            ].map((feat, i) => (
              <div key={i} className="bg-gradient-to-br from-indigo-50 to-violet-50 p-5 rounded-xl border border-indigo-100">
                <div className="text-2xl mb-3">{feat.icon}</div>
                <h4 className="font-bold text-slate-900 mb-2">{feat.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-14 max-w-5xl mx-auto"
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Frequently Asked Questions</h2>
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="space-y-4">
              {faqs.filter((_, i) => i % 2 === 0).map((faq) => (
                <div key={faq.id} className="border border-slate-200 rounded-xl bg-white">
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium text-slate-900 text-sm pr-4">
                      {faq.q}
                    </span>
                    <motion.div animate={{ rotate: openFaq === faq.id ? 180 : 0 }}>
                      <FaChevronDown className="text-slate-400" size={14} />
                    </motion.div>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === faq.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {faqs.filter((_, i) => i % 2 !== 0).map((faq) => (
                <div key={faq.id} className="border border-slate-200 rounded-xl bg-white">
                  <button
                    onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-medium text-slate-900 text-sm pr-4">
                      {faq.q}
                    </span>
                    <motion.div animate={{ rotate: openFaq === faq.id ? 180 : 0 }}>
                      <FaChevronDown className="text-slate-400" size={14} />
                    </motion.div>
                  </button>
                  <AnimatePresence initial={false}>
                    {openFaq === faq.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {checkoutPlan && (
          <CheckoutModal
            plan={checkoutPlan}
            billingCycle={billingCycle}
            tenantId={tenantId}
            onClose={() => setCheckoutPlan(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmPlan && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-[90%] max-w-md shadow-2xl text-left"
            >
              <div className="mb-4">
                <h3 className="text-lg font-bold text-slate-900">
                  Confirm Plan Change
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Please review your selection
                </p>
              </div>
              <div className="border rounded-xl p-4 mb-5 bg-slate-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">Plan</span>
                  <span className="font-semibold text-slate-900">
                    {confirmPlan.name}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-600">Billing</span>
                  <span className="font-semibold text-slate-900 capitalize">
                    {billingCycle}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Amount</span>
                  <span className="font-bold text-indigo-600">
                    {confirmPlan.price}
                  </span>
                </div>
              </div>
              {confirmPlan.isScheduled ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 text-sm text-amber-800">
                  <p className="font-semibold mb-1">Scheduled Change</p>
                  <p>
                    Your current plan remains active until{" "}
                    <span className="font-semibold">
                      {new Date(confirmPlan.effective_at).toLocaleDateString()}
                    </span>.
                  </p>
                  <p className="mt-2 text-xs">
                    Payment is required now.
                    Your new plan will activate automatically on that date.
                    Your current plan remains active until then.
                  </p>
                </div>
              ) : (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-5 text-sm text-emerald-800">
                  <p className="font-semibold mb-1">Immediate Activation</p>
                  <p>Your plan will start immediately after payment.</p>
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmPlan(null)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-600 font-semibold hover:bg-slate-200 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setCheckoutPlan(confirmPlan);
                    setConfirmPlan(null);
                  }}
                  className={`flex-1 py-2.5 rounded-xl font-semibold transition ${
                    confirmPlan.isScheduled
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:from-indigo-700 hover:to-violet-700"
                  }`}
                >
                  Continue to Pay
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}